import { Grid, SxProps } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import Separator from "./elements/Separator";
import Info from "./elements/Info";
import UnknownElement from "./elements/UnknownElement";
import Txtbox from "./elements/Txtbox";
import Txtarea from "./elements/Txtarea";
import Chkbox from "./elements/Chkbox";
import Combox from "./elements/Combox";
import OACCV1 from "./elements/OACCV1";
import OPTTV12 from "./elements/OPTTV12";
import OBUTV123 from "./elements/OBUTV123";
import Slide from "./elements/Slide";
import OINV1 from "./elements/OINV1";
import OSPV1 from "./elements/OSPV1";
import OPSTNV1 from "./elements/OPSTNV1";
import ORFIDLSV1 from "./elements/ORFIDLSV1";
import TRIGGERV1 from "./elements/TRIGGERV1";
import TRIGGERV2 from "./elements/TRIGGERV2";
import OTINENV1 from "./elements/OTINENV1";
import TWZV1 from "./elements/TWZV1";
import VEHCANV12 from "./elements/VEHCANV12";
import OSENV123 from "./elements/OSENV123";
import OTEMP1 from "./elements/OTEMP1";
import REMOTEFILE from "./elements/REMOTEFILE";
import Table from "./elements/Table";
import { TemplateDataProvider } from "../../../utils/TemplateDataProvider";
import { useTemplateRepeaterInfo } from "./TemplateBlockRepeater";
import { useUserInfo } from "../../../utils/user-info";
import {
  ButtonTemplateItem,
  ChkboxTemplate,
  ComboxTemplate,
  InfoTemplate,
  InputTemplateItem,
  PstnTemplateItem,
  RemotefileTemplate,
  RfidlistTemplateItem,
  SensorTemplateItem,
  SeparatorTemplate,
  SerialportTemplateItem,
  SlideTemplate,
  TableTemplate,
  TemplateItem,
  TempTemplateItem,
  ThermowatchTemplateItem,
  TinenTemplateItem,
  Trigger1TemplateItem,
  TxtTemplate,
  VehcanTemplateItem,
} from "@helios/toolbox-types";

interface TemplateElementsProp {
  configKey: string;
  items: TemplateItem[];
  disabled?: boolean;
  sx?: SxProps;
  postfix?: ReactNode;
  postfixSx?: SxProps;
  endUserKeys?: string[];
}

type TemplateItemProps = {
  item: TemplateItem;
  configKey: string;
  disabled?: boolean;
};
const TemplateItemComponent = ({ item, configKey, disabled }: TemplateItemProps) => {
  const { blockKey, index } = useTemplateRepeaterInfo();
  const keyPrefix = blockKey ? `${blockKey}.${index}.` : "";
  return (
    <TemplateDataProvider templateData={{ type: item.type, key: item.key !== undefined ? (blockKey ? `${blockKey}.${item.key}` : `${item.key}`) : item.key }}>
      {item.type === "SEPARATOR" ? (
        <Separator text={(item as SeparatorTemplate).text ?? ""} />
      ) : item.type === "INFO" ? (
        <Info text={(item as InfoTemplate).text} inlineTitle={item.inline} />
      ) : item.type === "TXTBOX" ? (
        <Txtbox
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          label={(item as TxtTemplate).label}
          hex={(item as TxtTemplate).hex}
          maxLength={(item as TxtTemplate).maxLength}
          number={(item as TxtTemplate).number}
          inputProps={(item as TxtTemplate).inputProps}
          enabledCharacters={(item as TxtTemplate).enabledCharacters}
          defaultValue={(item as TxtTemplate).defaultValue}
          notEmpty={(item as TxtTemplate).notEmpty}
          disabled={disabled}
        />
      ) : item.type === "TXTAREA" ? (
        <Txtarea configKey={configKey} elementKey={`${keyPrefix}${item.key}`} label={(item as TxtTemplate).label ?? ""} disabled={disabled} />
      ) : item.type === "CHKBOX" ? (
        <Chkbox configKey={configKey} elementKey={`${keyPrefix}${item.key}`} label={(item as ChkboxTemplate).label ?? ""} disabled={disabled} />
      ) : item.type === "COMBOX" ? (
        <Combox
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          label={(item as ComboxTemplate).label ?? ""}
          items={(item as ComboxTemplate).items ?? []}
          custom={(item as ComboxTemplate).custom}
          disabled={disabled}
        />
      ) : item.type === "SLIDE" ? (
        <Slide
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          label={(item as SlideTemplate).label ?? ""}
          maxValue={(item as SlideTemplate).maxValue ?? 100}
          disabled={disabled}
        />
      ) : item.type === "TABLE" ? (
        <Table
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          label={(item as TableTemplate).label ?? ""}
          rows={(item as TableTemplate).rows ?? 0}
          columns={(item as TableTemplate).columns ?? 0}
          disabled={disabled}
        />
      ) : item.type === "OACCV1" || item.type === "OACCFV1" ? (
        <OACCV1 configKey={configKey} overwriting={item.type === "OACCFV1"} disabled={disabled} />
      ) : ["OPTTV1", "OPTTV2"].includes(item.type) ? (
        <OPTTV12 configKey={configKey} version={item.type === "OPTTV1" ? 1 : 2} disabled={disabled} />
      ) : ["OBUTV1", "OBUTV2", "OBUTV3"].includes(item.type) ? (
        <OBUTV123
          configKey={configKey}
          version={item.type === "OBUTV1" ? 1 : item.type === "OBUTV2" ? 2 : 3}
          index={(item as ButtonTemplateItem).index}
          disabled={disabled}
        />
      ) : item.type === "OINV1" ? (
        <OINV1
          configKey={configKey}
          index={(item as InputTemplateItem).index}
          triggerItemsMask={(item as InputTemplateItem).triggerItemsMask}
          disabled={disabled}
        />
      ) : item.type === "OSPV1" ? (
        <OSPV1
          configKey={configKey}
          index={(item as SerialportTemplateItem).index}
          protocolItems={(item as SerialportTemplateItem).protocolItems}
          disabled={disabled}
        />
      ) : item.type === "OPSTNV1" ? (
        <OPSTNV1 configKey={configKey} index={(item as PstnTemplateItem).index} disabled={disabled} />
      ) : item.type === "ORFIDLSV1" ? (
        <ORFIDLSV1 configKey={configKey} maxCount={(item as RfidlistTemplateItem).maxCount ?? 0} disabled={disabled} />
      ) : item.type === "TRIGGERV1" ? (
        <TRIGGERV1
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          valueItems={(item as Trigger1TemplateItem).valueItems ?? []}
          disabled={disabled}
        />
      ) : item.type === "TRIGGERV2" ? (
        <TRIGGERV2 configKey={configKey} elementKey={`${keyPrefix}${item.key}`} disabled={disabled} />
      ) : item.type === "OTINENV1" ? (
        <OTINENV1
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          index={(item as TinenTemplateItem).index ?? 0}
          hasTriggers={(item as TinenTemplateItem).hasTriggers}
          disabled={disabled}
        />
      ) : item.type === "TWZV1" ? (
        <TWZV1
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          index={(item as ThermowatchTemplateItem).index ?? 0}
          min={(item as ThermowatchTemplateItem).min ?? 0}
          max={(item as ThermowatchTemplateItem).max ?? 0}
          disabled={disabled}
        />
      ) : ["VEHCANV1", "VEHCANV2"].includes(item.type) ? (
        <VEHCANV12
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          index={(item as VehcanTemplateItem).index ?? 0}
          version={item.type === "VEHCANV1" ? 1 : 2}
          disabled={disabled}
        />
      ) : ["OSENV1", "OSENV2", "OSENV3"].includes(item.type) ? (
        <OSENV123
          configKey={configKey}
          elementKey={`${keyPrefix}${item.key}`}
          index={(item as SensorTemplateItem).index ?? 0}
          version={(item as SensorTemplateItem).version ?? 3}
          typeItemsMask={(item as SensorTemplateItem).typeItemsMask ?? 0}
          disabled={disabled}
        />
      ) : item.type === "OTEMP1" ? (
        <OTEMP1 configKey={configKey} elementKey={`${keyPrefix}${item.key}`} index={(item as TempTemplateItem).index ?? 0} disabled={disabled} />
      ) : item.type === "REMOTEFILE" ? (
        <REMOTEFILE configKey={configKey} remotefiles={(item as RemotefileTemplate).remotefiles ?? []} disabled={disabled} />
      ) : (
        <UnknownElement configKey={configKey} elementKey={`${keyPrefix}${item.key}`} parameters={item} type={item.type} />
      )}
    </TemplateDataProvider>
  );
};

const TemplateElements = ({ configKey, items, disabled, postfix, postfixSx, sx, endUserKeys }: TemplateElementsProp) => {
  const [children, setChildren] = useState<ReactNode | null>(null);
  const [user] = useUserInfo();
  const limitedAccessUser = user?.rights.limited_user;

  useEffect(() => {
    const c = items.map((item, index) => {
      if (item.type === "BREAK") {
        return <Grid item key={`item-${index}`} xs={12} sx={{ height: 0, marginTop: -1 }} />;
      }
      return (
        <Grid
          item
          key={`item-${index}`}
          xs={item.inline ? undefined : 12}
          sx={!limitedAccessUser || !endUserKeys || (item.key && endUserKeys.includes(`${item.key}`)) ? { alignItems: "flex-end" } : { display: "none" }}
        >
          <TemplateItemComponent item={item} configKey={configKey} disabled={disabled} />
        </Grid>
      );
    });
    setChildren(c);
  }, [configKey, disabled, endUserKeys, items, limitedAccessUser]);

  return (
    <Grid container spacing={1} sx={{ padding: 1, ...sx }}>
      {children}
      <Grid item sx={postfixSx}>
        {postfix}
      </Grid>
    </Grid>
  );
};

export default TemplateElements;
